/**
 * Approved EUR (euro-cent) prices for ReHub products.
 *
 * HDRPhoto, ReHub credits (v1 + v2), custom credits, Active Spaces, and
 * ImageMotion double-credit EUR Stripe prices are live. Checkout uses the
 * dedicated Price IDs in those catalogues.
 *
 * HIOZ, Sketchplan, Tours new-model, 360Worlds Family/gifts, Doorsteps, and
 * Studio Build remain display-only until their Stripe products and Price IDs
 * exist. EUR never falls back to `price_data`. The fixed Sketchplan EUR
 * list lives in `sketchplan.ts` with the GBP and USD lists.
 */

import type { RehubCurrency } from './currency';

export const EUR_PRICING_STATUS = 'live_for_catalogued_products' as const;

export const PROPOSED_EUR_CREDIT_PACKS_V1 = {
  credits_10: { amountMinor: 990, perCredit: '€0.99' },
  credits_25: { amountMinor: 2400, perCredit: '€0.96' },
  credits_50: { amountMinor: 4900, perCredit: '€0.98' },
  credits_100: { amountMinor: 8900, perCredit: '€0.89' },
  credits_150: { amountMinor: 13500, perCredit: '€0.90' },
  credits_300: { amountMinor: 26900, perCredit: '€0.90' },
} as const;

export const PROPOSED_EUR_CREDIT_MONTHLY_V1 = {
  starter: { amountMinor: 2700, perCredit: '€0.90' },
  regular: { amountMinor: 5900, perCredit: '€0.79' },
  pro: { amountMinor: 8900, perCredit: '€0.71' },
  business: { amountMinor: 22900, perCredit: '€0.65' },
  agency: { amountMinor: 47900, perCredit: '€0.64' },
  enterprise: { amountMinor: 89900, perCredit: '€0.60' },
} as const;

export const PROPOSED_EUR_CUSTOM_PER_CREDIT_MINOR = 100; // €1.00

export const PROPOSED_EUR_CREDIT_PACKS_V2 = {
  credits_20: { amountMinor: 1800, perCredit: '€0.90' },
  credits_50_v2: { amountMinor: 4200, perCredit: '€0.84' },
  credits_150_v2: { amountMinor: 10800, perCredit: '€0.72' },
  credits_500: { amountMinor: 30000, perCredit: '€0.60' },
} as const;

export const PROPOSED_EUR_CREDIT_MONTHLY_V2 = {
  starter_v2: { amountMinor: 2300, perCredit: '€0.58' },
  // Grandfathered allowances (150 / 500). Do not change: existing subscribers.
  pro_v2: { amountMinor: 6900, perCredit: '€0.46' },
  studio_v2: { amountMinor: 17900, perCredit: '€0.36' },
  // v3 catalogue revision — same prices, reduced allowances (130 / 350).
  pro_v3: { amountMinor: 6900, perCredit: '€0.53' },
  studio_v3: { amountMinor: 17900, perCredit: '€0.51' },
} as const;

export const PROPOSED_EUR_ACTIVE_SPACES = {
  pro: 3500,      // £29 / $39 → €35
  agency: 7900,   // £69 / $79 → €79
  business: 15500, // £129 / $149 → €155
} as const;

export const PROPOSED_EUR_TOURS_PLANS = {
  standard: 5900,   // £49 / $59 → €59
  sales_pro: 15500, // £129 / $159 → €155
  ultra: 29900,     // £249 / $299 → €299
} as const;

export const PROPOSED_EUR_IMAGEMOTION_DOUBLE = {
  double_25: 2700, // £22 / $29 → €27
  double_50: 4900, // £40 / $55 → €49
} as const;

export const PROPOSED_EUR_HIOZ = {
  additionalMonthly: 5,
  additionalAnnual: 50,
  foundingMonthly: 5.99,
  starterMonthly: 11.99,
  hostMonthly: 23,
  proMonthly: 59,
  portfolioMonthly: 95,
} as const;

export const PROPOSED_EUR_WORLDS = {
  monthly: 5.99,
  annual: 34.99,
  launchAnnualIndividual: 17.99,
  launchAnnualFamily: 29.99,
  launchAnnualSoloTeacher: 45.99,
} as const;

export const PROPOSED_EUR_DOORSTEPS = {
  starterMonthly: 59,
  starterAnnual: 590,
  proMonthly: 119,
  proAnnual: 1190,
  agencyMonthly: 299,
  agencyAnnual: 2990,
} as const;

export function eurMinorForCreditPackV2(id: keyof typeof PROPOSED_EUR_CREDIT_PACKS_V2): number {
  return PROPOSED_EUR_CREDIT_PACKS_V2[id].amountMinor;
}

export function displayCurrencies(): RehubCurrency[] {
  return ['GBP', 'EUR', 'USD'];
}
